/* eslint no-param-reassign: "off", no-console: "off" */
const extractEntities = (payload) => {
  let entities = [];
  if (!payload) return entities;

  // based on entity group type we extract the type of entities we need
  switch (payload.entityType) {
    case 'remote':
      entities = payload.remotes || [];
      break;
    case 'device':
      entities = payload.devices || [];
      break;
    case 'subscription':
      entities = payload.subscriptions || [];
      break;
    default:
      entities = [];
      break;
  }
  return entities;
}

const groupStore = {
  state: {
    groups: [],
    count: 0,
    group: null,
    isLoadingGroups: false,
    isLoadingGroup: false,
    isRefreshingGroup: false,
    preSelectedEntity: null
  },
  mutations: {
    fetchGroups(state, payload) {
      state.groups = payload.payload ?? [];
      state.count = payload.count ?? 0;
    },
    updateGroup(state, payload) {
      const entities = extractEntities(payload);
      state.group = payload;
      state.group.entities = entities;
      
      // flatten to ids
      const groupsListIds = state.groups.map(g => g.id);
      // find index of group
      const groupIndex = groupsListIds.indexOf(state.group.id);
      if (groupIndex != -1) {
        // update the groups list view
        const groupListModel = {
          id: state.group.id,
          name: state.group.name,
          entityType: state.group.entityType,
          ownerId: state.group.ownerId,
          sharedWith: state.group.sharedWith,
          entities: state.group.entities
        }
        // replace the existing group in the list with the updated values
        state.groups.splice(groupIndex, 1, groupListModel);
      }
    },
    loadGroup(state, payload) {
      if (!payload) return;
      
      const entities = extractEntities(payload);
      state.group = payload;
      state.group.entities = entities;
    },
    addGroup(state, payload) {
      state.group = payload;

      // add to list page
      state.groups.push({
        id: payload.id,
        name: payload.name,
        entityType: payload.entityType,
        ownerId: payload.ownerId,
        sharedWith: payload.sharedWith,
        entities: payload.entities
      });
    },
    deleteGroup(state, deletedGroup) {
      state.group = null;

      // find the index of the group in the list page
      const groupIndex = state.groups.findIndex(g => g.id == deletedGroup.id);
      if (groupIndex > -1) {
        // remove the group from the list page
        state.groups.splice(groupIndex, 1);
      }
    },
    assignPreselectedEntity(state, entity) {
      state.preSelectedEntity = entity;
    }
  },
  actions: {
    /**
     * Fetches a list of groups
     * @param {*} param0 
     * @param {Object} payload - Object containing paging, sorting, filters
     */
    // eslint-disable-next-line no-unused-vars
    async fetchGroups({ state, commit }, payload) {
      try {
        // set indicator to signify fetching of groups
        state.isLoadingGroups = true;
        // send the request
        const res = await fetch('/v2/groups');
        // catch failures
        if (!res.ok) throw new Error(await res.text());
        // extract the data from the response
        const data = await res.json();
        // mutate!
        commit('fetchGroups', data);
        // set indicator to signify fetching of groups is complete
        state.isLoadingGroups = false;
        return data;
      } catch (ex) {
        console.error(ex);
        state.isLoadingGroups = false;
        return false;
      }
    },
    /**
     * Fetches an individual group
     * @param {*} param0 
     * @param {string} groupId - Individual Group Id
     */
    async fetchGroup({ state, commit }, groupId) {
      state.isLoadingGroup = true;
      // Try to fetch the remote from idxdb first
      try {
        // eslint-disable-next-line no-undef
        const result = await $groupStore.getItem(groupId);
        // If a remote is found in idxdb
        if (result != null) {
          // Commit it so the page loads
          commit('loadGroup', result);
          // Stop the loading
          state.isLoadingGroup = false;
          // Start the refresh
          state.isRefreshingGroup = true;
        }
      } catch (ex) {
        // If there's an error, failover to API
        console.error(ex);
      }

      try {
        const res = await fetch(`/v2/groups/${groupId}`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        commit('loadGroup', data);
        state.isLoadingGroup = false;
        state.isRefreshingGroup = false;
        return data;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async addGroup({ commit }, payload) {
      try {
        const res = await fetch('/v2/groups', {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) throw new Error(await res.text());
        
        // extract the new group and update vuex state
        const addedGroup = await res.json();
        commit('addGroup', addedGroup);
        
        // return the new group
        return { isSuccess: true, addedGroup };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, err: ex };
      }
    },
    async updateGroup({ commit }, payload) {
      try {
        const res = await fetch(`/v2/groups/${payload.id}`, {
          body: JSON.stringify(payload),
          method: 'PUT',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) throw new Error(await res.text());
        
        // fetch the updated group and all meatadata
        const updatedGroup = await res.json();
        commit('updateGroup', updatedGroup);
        // return the new group
        return { isSuccess: true, updatedGroup };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, err: ex };
      }
    },
    async deleteGroup({ commit }, group) {
      try {
        const res = await fetch(`/v2/groups/${group.id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error(await res.text());
        
        // extract the new group and update vuex state
        const isDeleted = await res.json();
        if (!isDeleted) throw new Error('Error encountered while attempting to delete group.');
        commit('deleteGroup', group);
        
        // return the new group
        return { isSuccess: true, group };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, err: ex };
      }
    }
  }
}
export default groupStore;
